import React from 'react';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Privacy = () => {
  const privacyContent = `
# Privacy Policy

**AY Productions Ltd trading as AI Colab Studios**
Last updated: June 2026

---

## 1. Who we are

This Privacy Policy explains how AY Productions Ltd trading as AI Colab Studios ("we", "us", "our") collects, uses and protects your personal data when you visit our website at www.aicolabstudio.com, purchase our digital products, sign up to our newsletter, or engage us for consultancy services.

AY Productions Ltd is the data controller for the purposes of UK GDPR and the Data Protection Act 2018.

**Contact details:**
Email: info@aicolabstudio.com
Website: www.aicolabstudio.com

If you have any questions about this Privacy Policy or how we handle your personal data, please contact us at info@aicolabstudio.com.

---

## 2. What personal data we collect

We may collect and process the following personal data about you:

| Category | Examples |
|---|---|
| **Identity data** | Your first name, last name, business name |
| **Contact data** | Email address, postal address, telephone number |
| **Transaction data** | Details of products or services you have purchased from us |
| **Financial data** | Payment card details (processed securely by Stripe — we do not store card details ourselves) |
| **Technical data** | IP address, browser type and version, time zone setting, browser plug-in types, operating system and platform |
| **Usage data** | Information about how you use our website and services |
| **Marketing and communications data** | Your preferences in receiving marketing from us and your communication preferences |

We do not collect any special categories of personal data (such as data about your race, ethnicity, religious beliefs, health, or sexual orientation).

---

## 3. How we collect your personal data

We collect personal data from you in the following ways:

**Directly from you**, when you:
- Purchase a digital product (e-book, prompt pack) from our website
- Sign up to our newsletter or mailing list
- Contact us by email or via our website contact form
- Engage us for consultancy or coaching services
- Respond to a survey or provide feedback

**Automatically**, when you visit our website, through cookies and similar technologies (see our Cookie section below).

**From third parties**, including:
- **Stripe** — our payment processor, who provides us with confirmation of payment transactions
- **Supabase** — our database and authentication provider

---

## 4. How we use your personal data

We use your personal data for the following purposes:

| Purpose | Legal basis |
|---|---|
| To process and fulfil your purchase of digital products | Performance of a contract |
| To deliver services you have engaged us to provide | Performance of a contract |
| To send you your purchased digital downloads | Performance of a contract |
| To send you our newsletter and marketing emails (where you have subscribed) | Consent |
| To respond to your enquiries and provide customer support | Legitimate interests |
| To improve our website and services | Legitimate interests |
| To comply with our legal obligations | Legal obligation |
| To prevent fraud and ensure the security of our website | Legitimate interests |

**Marketing:** We will only send you marketing emails if you have given us your consent to do so (for example, by signing up to our newsletter). You can withdraw your consent and unsubscribe at any time by clicking the unsubscribe link in any of our emails or by emailing us at info@aicolabstudio.com.

---

## 5. Who we share your personal data with

We may share your personal data with the following third parties:

- **Stripe** (stripe.com) — our payment processor. Stripe processes your payment card details securely. We do not store your card details. Stripe's privacy policy is available at https://stripe.com/gb/privacy.
- **Supabase** (supabase.com) — our database and authentication provider, used to manage your account and purchase records. Supabase's privacy policy is available at https://supabase.com/privacy.
- **Email service providers** — we may use third-party email platforms to send newsletters and transactional emails.
- **Professional advisers** — including lawyers, accountants and insurers where necessary.
- **Regulatory authorities** — where we are required to do so by law.

We do not sell your personal data to any third party.

---

## 6. International transfers

Some of our third-party service providers (including Stripe and Supabase) may process your personal data outside the United Kingdom. Where this occurs, we ensure that appropriate safeguards are in place, such as the UK International Data Transfer Agreement or Standard Contractual Clauses, to protect your personal data in accordance with UK GDPR.

---

## 7. How long we keep your personal data

We retain your personal data only for as long as necessary for the purposes set out in this Privacy Policy, or as required by law.

| Data type | Retention period |
|---|---|
| Purchase and transaction records | 7 years (for tax and accounting purposes) |
| Newsletter subscriber data | Until you unsubscribe or withdraw consent |
| Consultancy client records | 7 years from the end of the engagement |
| Website enquiry data | 2 years |
| Technical/usage data | 12 months |

---

## 8. Your rights

Under UK GDPR, you have the following rights in relation to your personal data:

- **Right of access** — you can request a copy of the personal data we hold about you.
- **Right to rectification** — you can ask us to correct inaccurate or incomplete data.
- **Right to erasure** — you can ask us to delete your personal data in certain circumstances.
- **Right to restrict processing** — you can ask us to restrict how we use your data in certain circumstances.
- **Right to data portability** — you can ask us to provide your data in a structured, machine-readable format.
- **Right to object** — you can object to our processing of your data where we rely on legitimate interests as our legal basis.
- **Right to withdraw consent** — where we rely on your consent, you can withdraw it at any time.

To exercise any of these rights, please contact us at info@aicolabstudio.com. We will respond to your request within one month.

You also have the right to lodge a complaint with the **Information Commissioner's Office (ICO)** at www.ico.org.uk if you believe we have not handled your personal data in accordance with the law.

---

## 9. Cookies

Our website uses cookies to improve your experience. Cookies are small text files placed on your device when you visit our website.

We use the following types of cookies:

| Cookie type | Purpose |
|---|---|
| **Essential cookies** | Necessary for the website to function (e.g. session management) |
| **Analytics cookies** | Help us understand how visitors use our website so we can improve it |
| **Marketing cookies** | Used to show you relevant content and track the effectiveness of our marketing |

You can control and delete cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website.

---

## 10. Security

We take appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, destruction or alteration. Our payment processing is handled entirely by Stripe, which is PCI DSS compliant, meaning your card details are never transmitted to or stored on our servers.

---

## 11. Third-party links

Our website may contain links to third-party websites. We are not responsible for the privacy practices of those websites and encourage you to read their privacy policies.

---

## 12. Changes to this Privacy Policy

We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the updated policy on our website with a revised "last updated" date. We encourage you to review this page periodically.

---

## 13. Contact us

If you have any questions, concerns or requests regarding this Privacy Policy or how we handle your personal data, please contact us:

**AY Productions Ltd trading as AI Colab Studios**
Email: info@aicolabstudio.com
Website: www.aicolabstudio.com
`;

  return (
    <MainLayout>
      <Section bg="cream" className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-brand-navy mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: June 2026</p>
        </div>
      </Section>
      <Section bg="white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-teal max-w-none">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-brand-navy mb-8" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-brand-navy mt-12 mb-6" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-xl font-bold text-brand-navy mt-8 mb-4" {...props} />,
                p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-6" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700" {...props} />,
                li: ({node, ...props}) => <li className="leading-relaxed" {...props} />,
                table: ({node, ...props}) => (
                  <div className="overflow-x-auto mb-8">
                    <table className="w-full border-collapse border border-gray-200" {...props} />
                  </div>
                ),
                th: ({node, ...props}) => <th className="border border-gray-200 bg-gray-50 p-3 text-left font-bold text-brand-navy" {...props} />,
                td: ({node, ...props}) => <td className="border border-gray-200 p-3 text-gray-700" {...props} />,
                hr: ({node, ...props}) => <hr className="my-12 border-gray-200" {...props} />,
              }}
            >
              {privacyContent}
            </ReactMarkdown>
          </div>
        </div>
      </Section>
    </MainLayout>
  );
};

export default Privacy;